import { z } from 'zod';
import { isValidTtsVoice } from './tts-voices.js';
/**
 * Which Wildix "kite" links the web UI should render on a contact-profile.
 * Kite URLs are public per-user deep links; this just chooses which to show.
 *   - collab7: classic Wildix Collaboration 7 chat kite
 *   - x-bees:  x-bees scheduling kite
 *   - both:    show both (default — kites are harmless)
 *   - none:    hide; tenant doesn't use Wildix front-ends
 */
export const WildixKiteProviderSchema = z.enum(['collab7', 'x-bees', 'both', 'none']);
/**
 * CustomerConfig — per-customer settings powering dialplan-var injection,
 * caller-ID, branding, and TTS/audio defaults.
 *
 * One row per customer (`customer_id` is PK + FK). Created in migration
 * 0004_customer_config.sql. Mirrored as Drizzle `customerConfig` in
 * packages/app/src/db/schema.ts.
 *
 * Sensitive fields:
 *   - wildix_rest_userpwd — "user:password" for the on-PBX dialplan's CURL
 *     DNC checks. NEVER log this raw, only masked length.
 */
export const CustomerConfigSchema = z.object({
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    // Phone routing
    country_code: z.string().min(1).default('1'),
    prefix_nat: z.string().min(1).default('1'),
    prefix_int: z.string().min(1).default('011'),
    international_default: z.boolean().default(false),
    // Caller ID (CLASSOUND-validated)
    cid_name: z.string().min(1),
    cid_number: z.string().min(1),
    // Wildix REST auth (sensitive)
    wildix_rest_userpwd: z.string().nullable().optional(),
    // TTS + audio defaults
    tts_voice_default: z
        .string()
        .default('Danielle')
        .refine((v) => isValidTtsVoice(v), {
        message: 'Unknown TTS voice — must be one of the supported PBX voices',
    }),
    intro_message_default: z.string().nullable().optional(),
    closing_message_default: z.string().nullable().optional(),
    // Company metadata
    co_name: z.string().nullable().optional(),
    co_address: z.string().nullable().optional(),
    co_city: z.string().nullable().optional(),
    co_state: z.string().nullable().optional(),
    co_postal: z.string().nullable().optional(),
    co_logo: z.string().nullable().optional(),
    co_email: z.string().nullable().optional(),
    co_tel: z.string().nullable().optional(),
    co_fax: z.string().nullable().optional(),
    co_from: z.string().nullable().optional(),
    co_timezone: z.string().nullable().optional(),
    co_web: z.string().nullable().optional(),
    co_privacy: z.string().nullable().optional(),
    co_terms: z.string().nullable().optional(),
    // Spool / eventfile config
    eventfile_dir: z.string().min(1).default('/var/lib/xsc-connector/eventfiles'),
    /** Phase 5b — admin toggle: allow non-admin users to connect their own
     *  email accounts (Gmail OAuth, etc). Admins can always manage shared
     *  email accounts regardless. */
    allow_user_email_connect: z.boolean().default(true),
    /** Which Wildix kite links to surface on contact-profile pages. */
    wildix_kite_provider: WildixKiteProviderSchema.default('both'),
    /** Phase 6 — Wildix Cloud adapter creds (envelope-encrypted JSON of
     *  { apiBaseUrl, apiKey | clientId+clientSecret, smsGatewayId? }). When
     *  present, the PbxAdapterFactory dispatches voice/SMS through the cloud
     *  REST surface instead of the on-PBX long-poll connector. Always
     *  returned masked over the wire — see CustomerConfigMaskedSchema. */
    wildix_cloud_creds_encrypted: z.string().nullable().optional(),
    /** Phase 6 — HMAC secret for verifying inbound Wildix webhook payloads.
     *  Set by the admin when configuring webhooks; rotation invalidates all
     *  previously-signed payloads. Never returned raw over the wire. */
    wildix_webhook_secret: z.string().nullable().optional(),
    /** Phase 6 — per-customer canonical webhook URL token. The Wildix admin
     *  pastes `<ISSUER>/v1/wildix/webhook/<token>` into their Cloud setup.
     *  The shared `/v1/wildix/webhook` URL is the fallback (resolves tenant
     *  by `pbx`/`company` body field). */
    wildix_webhook_token: z.string().nullable().optional(),
    // Audit
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
/**
 * Update payload — everything the admin UI can change. customer_id is in the
 * URL, audit timestamps are server-managed.
 */
export const CustomerConfigUpdateSchema = CustomerConfigSchema.omit({
    customer_id: true,
    created_at: true,
    updated_at: true,
}).partial();
/**
 * Response shape with the Wildix REST credential masked. The full value is
 * only ever returned to admin GET (so the UI can edit it) — but logging this
 * struct is always safe.
 */
export const CustomerConfigMaskedSchema = CustomerConfigSchema.extend({
    wildix_rest_userpwd: z.string().nullable().optional(),
    wildix_cloud_creds_encrypted: z.string().nullable().optional(),
    wildix_webhook_secret: z.string().nullable().optional(),
}).transform((v) => ({
    ...v,
    wildix_rest_userpwd: v.wildix_rest_userpwd
        ? `**** (length=${v.wildix_rest_userpwd.length})`
        : null,
    wildix_cloud_creds_encrypted: v.wildix_cloud_creds_encrypted ? '**** (configured)' : null,
    wildix_webhook_secret: v.wildix_webhook_secret ? '**** (configured)' : null,
}));
/**
 * Mask a Wildix REST credential for logging. Always use this — never log raw.
 */
export function maskWildixUserpwd(value) {
    if (!value)
        return '(unset)';
    return `**** (length=${value.length})`;
}
//# sourceMappingURL=customer-config.js.map